import { useState, useMemo } from 'react'
import {
  BookUser, Plus, Search, Phone, X, Upload, Download, Trash2,
  Mail, FileText, Edit3, Check, Tag
} from 'lucide-react'
import usePhonebookStore, { parseCsv, parseVcf, formatPhone } from '../stores/phonebookStore'
import useSipStore from '../stores/sipStore'

function ContactDialog({ existing, onClose, onSave }) {
  const [name, setName] = useState(existing?.name || '')
  const [phones, setPhones] = useState(existing?.phones?.join(', ') || '')
  const [email, setEmail] = useState(existing?.email || '')
  const [notes, setNotes] = useState(existing?.notes || '')
  const [tags, setTags] = useState((existing?.tags || []).join(', '))

  const handleSave = () => {
    const phoneList = phones.split(/[,;]/).map(p => p.trim()).filter(Boolean)
    if (phoneList.length === 0) {
      alert('צריך לפחות מספר טלפון אחד')
      return
    }
    onSave({
      name: name.trim() || phoneList[0],
      phones: phoneList,
      email: email.trim(),
      notes: notes.trim(),
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-panel border border-line rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2">
            <BookUser size={16} className="text-brand" />
            {existing ? 'עריכת איש קשר' : 'איש קשר חדש'}
          </h2>
          <button onClick={onClose} className="text-mute hover:text-text">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-mute text-xs mb-1">שם *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="דוד כהן"
              className="w-full px-3 py-2 bg-bg border border-line rounded-lg"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-mute text-xs mb-1">
              טלפונים *  <span className="opacity-60">(הפרד ב-, או ;)</span>
            </label>
            <input
              type="text"
              value={phones}
              onChange={(e) => setPhones(e.target.value)}
              placeholder="0501234567, 0732745593"
              className="w-full px-3 py-2 bg-bg border border-line rounded-lg ltr tech"
            />
          </div>

          <div>
            <label className="block text-mute text-xs mb-1">אימייל</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              className="w-full px-3 py-2 bg-bg border border-line rounded-lg ltr"
            />
          </div>

          <div>
            <label className="block text-mute text-xs mb-1">תגיות <span className="opacity-60">(מופרדות ב-,)</span></label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="לקוח VIP, ספק"
              className="w-full px-3 py-2 bg-bg border border-line rounded-lg"
            />
          </div>

          <div>
            <label className="block text-mute text-xs mb-1">הערות</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="פרטים נוספים..."
              rows={3}
              className="w-full px-3 py-2 bg-bg border border-line rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-line hover:bg-line/30 text-sm">
            ביטול
          </button>
          <button onClick={handleSave} className="flex-1 py-2 rounded-lg bg-brand hover:bg-brand/90 text-slate-900 font-medium text-sm">
            {existing ? 'עדכן' : 'הוסף'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ImportDialog({ onClose, onImport }) {
  const [text, setText] = useState('')
  const [preview, setPreview] = useState([])
  const [format, setFormat] = useState('csv')

  const updatePreview = (newText, fmt = format) => {
    setText(newText)
    const rows = fmt === 'vcf' ? parseVcf(newText) : parseCsv(newText)
    setPreview(rows.slice(0, 50))
  }

  const handleFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const content = String(reader.result || '')
      const fmt = file.name.toLowerCase().endsWith('.vcf') ? 'vcf' : 'csv'
      setFormat(fmt)
      updatePreview(content, fmt)
    }
    reader.readAsText(file)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-panel border border-line rounded-2xl p-5 max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2">
            <Upload size={16} className="text-brand" />
            ייבוא אנשי קשר
          </h2>
          <button onClick={onClose} className="text-mute hover:text-text">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 text-sm flex-1 overflow-y-auto">
          <div className="flex items-center gap-2">
            <label className="px-3 py-2 rounded-lg bg-brand hover:bg-brand/90 text-slate-900 text-xs font-medium cursor-pointer">
              <Upload size={12} className="inline ms-1" /> בחר קובץ
              <input type="file" accept=".csv,.txt,.vcf" onChange={handleFile} className="hidden" />
            </label>
            <select
              value={format}
              onChange={(e) => { setFormat(e.target.value); updatePreview(text, e.target.value) }}
              className="px-3 py-2 bg-bg border border-line rounded-lg text-sm"
            >
              <option value="csv">CSV</option>
              <option value="vcf">vCard (.vcf)</option>
            </select>
            <span className="text-xs text-mute">או הדבק למטה:</span>
          </div>

          <textarea
            value={text}
            onChange={(e) => updatePreview(e.target.value, format)}
            placeholder={format === 'csv'
              ? 'name,phone,email\nדוד כהן,0501234567,david@example.com\n...'
              : 'BEGIN:VCARD\nFN:דוד כהן\nTEL:0501234567\nEND:VCARD'
            }
            rows={6}
            className="w-full px-3 py-2 bg-bg border border-line rounded-lg font-mono text-xs ltr"
          />

          {preview.length > 0 && (
            <div>
              <div className="text-xs text-mute mb-1">תצוגה מקדימה ({preview.length} אנשי קשר)</div>
              <div className="border border-line rounded-lg max-h-48 overflow-y-auto">
                {preview.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 border-b border-line/40 last:border-0 text-xs">
                    <span className="flex-1 truncate">{p.name || '(ללא שם)'}</span>
                    <span className="ltr tech text-mute">{p.phones.join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-line hover:bg-line/30 text-sm">
            ביטול
          </button>
          <button
            onClick={() => { onImport(preview); onClose() }}
            disabled={preview.length === 0}
            className="flex-1 py-2 rounded-lg bg-brand hover:bg-brand/90 disabled:bg-line text-slate-900 font-medium text-sm disabled:text-mute"
          >
            ייבא {preview.length} אנשי קשר
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Phonebook() {
  const contacts = usePhonebookStore(s => s.contacts)
  const add = usePhonebookStore(s => s.add)
  const update = usePhonebookStore(s => s.update)
  const remove = usePhonebookStore(s => s.remove)
  const importMany = usePhonebookStore(s => s.importMany)
  const exportCsv = usePhonebookStore(s => s.exportCsv)
  const sipDial = useSipStore(s => s.dial)
  const sipStatus = useSipStore(s => s.status)
  const sipInCall = useSipStore(s => s.inCall)

  const [query, setQuery] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editing, setEditing] = useState(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return contacts
    const qDigits = q.replace(/[^\d]/g, '')
    return contacts.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.tags?.some(t => t.toLowerCase().includes(q)) ||
      (qDigits.length >= 3 && c.phones.some(p => p.includes(qDigits)))
    )
  }, [contacts, query])

  const downloadCsv = () => {
    const csv = exportCsv()
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `kaliai-contacts-${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const dial = (phone) => {
    if (sipStatus !== 'registered' || sipInCall) return
    try { sipDial(phone) } catch (e) { console.error(e) }
  }

  return (
    <div className="bg-panel border border-line rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-text flex items-center gap-2">
          <BookUser size={16} className="text-brand" />
          אנשי קשר <span className="text-xs text-mute">({contacts.length})</span>
        </h2>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowImport(true)}
            className="p-1.5 rounded-lg hover:bg-line/30 text-mute hover:text-text"
            title="ייבוא"
          >
            <Upload size={14} />
          </button>
          <button
            onClick={downloadCsv}
            disabled={contacts.length === 0}
            className="p-1.5 rounded-lg hover:bg-line/30 text-mute hover:text-text disabled:opacity-30"
            title="ייצוא CSV"
          >
            <Download size={14} />
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-brand hover:bg-brand/90 text-slate-900 text-xs font-medium"
          >
            <Plus size={12} /> חדש
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-mute" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="חפש לפי שם / טלפון / תגית..."
          className="w-full pe-9 ps-3 py-2 bg-bg border border-line rounded-lg text-sm"
        />
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center text-mute py-8 text-sm">
          <BookUser size={28} className="mx-auto mb-2 opacity-40" />
          {contacts.length === 0 ? 'אין אנשי קשר עדיין' : 'לא נמצא'}
          {contacts.length === 0 && (
            <div className="text-xs mt-2 space-y-1">
              <div>
                <button onClick={() => setShowAdd(true)} className="text-brand hover:underline">+ הוסף איש קשר</button>
              </div>
              <div>
                <button onClick={() => setShowImport(true)} className="text-brand hover:underline">⬆ ייבא רשימה</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {filtered.map(c => (
            <div key={c.id} className="border border-line/40 rounded-lg p-2 hover:bg-line/20 group">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-brand/20 text-brand text-sm font-semibold flex items-center justify-center shrink-0">
                  {c.name.slice(0, 1) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                    {c.phones.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => dial(p)}
                        disabled={sipStatus !== 'registered' || sipInCall}
                        className="ltr tech text-cyan-300 hover:text-brand hover:underline disabled:text-mute disabled:no-underline flex items-center gap-0.5"
                        title="חייג"
                      >
                        <Phone size={9} />
                        {formatPhone(p)}
                      </button>
                    ))}
                  </div>
                  {c.email && (
                    <div className="text-[11px] text-mute ltr tech truncate">
                      <Mail size={9} className="inline ms-0.5" /> {c.email}
                    </div>
                  )}
                  {c.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.tags.map((t, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-line/40 text-mute">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex items-start gap-0.5 shrink-0">
                  <button
                    onClick={() => setEditing(c)}
                    className="p-1 text-mute hover:text-text"
                    title="עריכה"
                  >
                    <Edit3 size={12} />
                  </button>
                  <button
                    onClick={() => { if (confirm(`למחוק את ${c.name}?`)) remove(c.id) }}
                    className="p-1 text-mute hover:text-rose-400"
                    title="מחיקה"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <ContactDialog
          onClose={() => setShowAdd(false)}
          onSave={(c) => add(c)}
        />
      )}

      {editing && (
        <ContactDialog
          existing={editing}
          onClose={() => setEditing(null)}
          onSave={(c) => update(editing.id, c)}
        />
      )}

      {showImport && (
        <ImportDialog
          onClose={() => setShowImport(false)}
          onImport={(rows) => {
            const r = importMany(rows)
            alert(`נוספו ${r.added} אנשי קשר${r.skipped > 0 ? ` (${r.skipped} דולגו - קיימים)` : ''}`)
          }}
        />
      )}
    </div>
  )
}
